import { DriveItemListAction, DriveItemListActionKeys } from './actions';
import { DriveItemListDisplayType } from './constants';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import LocalStorageManager from '../../utils/localStorage/manager';
import UserAgentUtils from '../../utils/userAgent';
import { I18n } from 'react-redux-i18n';

export interface DriveListState {
	driveItemsFetchStatus: { [k: number]: DriveItemFetchState};
	driveItems: { [k: number]: DriveItemInfo };
	resolutions: { [k: string]: Api.ResolutionGroup };
	displayType: DriveItemListDisplayType;
	fetchAllFetchStatus: FetchStatus;
}

export interface DriveItemInfo extends Api.DriveFolderInfo {
	isInGallery?: boolean;
}

export enum FetchStatus {
	NOT_FETCHED = 'NOT_FETCHED',
	FETCHING = 'FETCHING',
	FETCHED = 'FETCHED',
	ERROR = 'ERROR'
}

let displayType;
switch (LocalStorageManager.getDisplayType()) {
	case 'Row':
		displayType = DriveItemListDisplayType.Row;
		break;
	case 'Grid':
		displayType = DriveItemListDisplayType.Grid;
		break;
	default:
		displayType = DriveItemListDisplayType.Masonry;
}

const initialState: DriveListState = {
	driveItemsFetchStatus: {},
	driveItems: {},
	resolutions: {},
	fetchAllFetchStatus: FetchStatus.NOT_FETCHED,
	displayType
};

export interface DriveItemFetchState {
	metaFetchStatus: FetchStatus;
	contentBlocksStatus: { [k: number]: FetchStatus };
}

const driveItemFolderInitialState: DriveItemFetchState = {
	metaFetchStatus: FetchStatus.NOT_FETCHED,
	contentBlocksStatus: {}
};

export function driveList(state: DriveListState = initialState, action: DriveItemListAction): DriveListState {
	if (action.type === DriveItemListActionKeys.UPDATE_DRIVE_ITEM_LIST_DISPLAY_TYPE) {
		return { ...state, displayType: action.displayType };
	}

	let newState = Utils.deepCopy(state);
	let driveListItem;
	let driveItemId;

	if (action.type === DriveItemListActionKeys.REQUEST_DRIVE_META
	|| action.type === DriveItemListActionKeys.RECEIVE_DRIVE_META
	|| action.type === DriveItemListActionKeys.FETCH_ERROR_DRIVE_META) {
		driveItemId = action.driveItemId;
	}

	if (action.type === DriveItemListActionKeys.REQUEST_CONTENT_BLOCK
	|| action.type === DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK
	|| action.type === DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK) {
		driveItemId = action.driveFolderId;
	}

	if (driveItemId) {
		driveListItem = getDriveItemStatusFromMap(newState.driveItemsFetchStatus, driveItemId);
	}

	switch (action.type) {
		case DriveItemListActionKeys.REQUEST_DRIVE_META:
			driveListItem.metaFetchStatus = FetchStatus.FETCHING;
			break;
		case DriveItemListActionKeys.RECEIVE_DRIVE_META:
			driveListItem.metaFetchStatus = FetchStatus.FETCHED;
			break;
		case DriveItemListActionKeys.FETCH_ERROR_DRIVE_META:
			driveListItem.metaFetchStatus = FetchStatus.ERROR;
			break;
		case DriveItemListActionKeys.REQUEST_CONTENT_BLOCK:
			driveListItem.contentBlocksStatus[action.block] = FetchStatus.FETCHING;
			break;
		case DriveItemListActionKeys.RECEIVE_CONTENT_BLOCK:
			driveListItem.contentBlocksStatus[action.block] = FetchStatus.FETCHED;
			break;
		case DriveItemListActionKeys.FETCH_ERROR_CONTENT_BLOCK:
			driveListItem.contentBlocksStatus[action.block] = FetchStatus.ERROR;
			break;
		case DriveItemListActionKeys.RECEIVE_DRIVE_ITEMS:
			let driveItemsInfos: { [k: number]: DriveItemInfo } = {};
			for (driveItemId in action.driveItems) {
				if (action.driveItems.hasOwnProperty(driveItemId)) {
					driveItemsInfos[driveItemId] = action.driveItems[driveItemId];
					// if isInGallery does not exist, use the default one sent
					// (for example it exists in the save in localstorage)
					if (driveItemsInfos[driveItemId].isInGallery === undefined) {
						driveItemsInfos[driveItemId].isInGallery = action.isInGallery;
					}

					// if the driveItem is the private directory, it will be renamed to localized "My drive"
					if (action.multiAppLogin && driveItemsInfos[driveItemId].type === Api.DriveItemType.Directory) {
						driveItemsInfos[driveItemId].label = getFolderLabel(action.driveItems[driveItemId], action.multiAppLogin);
					}
				}
			}
			newState.driveItems = mergeDriveItems(newState.driveItems, driveItemsInfos);
			break;
		case DriveItemListActionKeys.RECEIVE_RESOLUTIONS:
			newState.resolutions = mergeResolutions(newState.resolutions, action.resolutions);
			break;
		case DriveItemListActionKeys.REQUEST_ALL:
			newState.fetchAllFetchStatus = FetchStatus.FETCHING;
			break;
		case DriveItemListActionKeys.RECEIVE_ALL:
			newState.fetchAllFetchStatus = FetchStatus.FETCHED;
			break;
		default:
			return state;
	}

	if (driveItemId) {
		newState.driveItemsFetchStatus[driveItemId] = driveListItem;
	}

	if (Utils.stringIsInStringEnum(action.type, DriveItemListActionKeys)) {
		if (UserAgentUtils.isOnline() && UserAgentUtils.isMobileApp) {
			LocalStorageManager.setStoreDriveList(newState);
		}
	}

	return newState;
}

function getFolderLabel(driveItem: DriveItemInfo, multiAppLogin: string): string {
	return driveItem.label === multiAppLogin ? I18n.t(`messages.my_documents`) : driveItem.label;
}

// Function to merge maps of drive items that ensure no information is overriden
function mergeDriveItems(map1: { [k: number]: DriveItemInfo }, map2: { [k: number]: DriveItemInfo }): { [k: number]: DriveItemInfo } {
	let resultMap: { [k: number]: DriveItemInfo } = Utils.deepCopy(map1);

	Object.keys(map2).forEach(key => {
		const id = parseInt(key, 10);
		if (resultMap[id]) {
			const itemFromMap2 = map2[id];

			if (itemFromMap2.resources) {
				resultMap[id] = Utils.deepCopy(map2[id]);
			}
		} else {
			resultMap[id] = Utils.deepCopy(map2[id]);
		}
	});
	return resultMap;
}

// Function to merge maps of resolutions that ensure no resolution is overriden
function mergeResolutions(map1: { [k: string]: Api.ResolutionGroup }, map2: { [k: string]: Api.ResolutionGroup }): { [k: string]: Api.ResolutionGroup } {
	let resultMap: { [k: string]: Api.ResolutionGroup } = Utils.deepCopy(map1);

	Object.keys(map2).forEach(key => {
		if (resultMap[key]) {
			const itemFromMap2 = map2[key];
			const itemFromResultMap = resultMap[key];

			if (itemFromMap2.sizes && itemFromResultMap.sizes
				&& itemFromMap2.sizes.length > itemFromResultMap.sizes.length) {
				resultMap[key] = Utils.deepCopy(map2[key]);
			}
		} else {
			resultMap[key] = Utils.deepCopy(map2[key]);
		}
	});
	return resultMap;
}

export function getDriveItemStatusFromMap(driveItemsFetchStatus: { [k: number]: DriveItemFetchState }, folderId: number): DriveItemFetchState {
	return driveItemsFetchStatus[folderId] ? driveItemsFetchStatus[folderId] : Utils.deepCopy(driveItemFolderInitialState);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/DriveItemList/reducer.ts